import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isNum from 'is-number';
import _groupBy from 'lodash/groupBy';

import { Box, Flex, Image, Number, SplitScore, Text } from '../Base';
import { COLOR, SPACE } from '../Theme';
import fillArray from '../../Util/fillArray';
import getConfig from '../../Util/Config';
import ScoreBar from '../ScoreBar';

import { Table, TableBody, TableHead, TableCell, TableRow } from './TableComponents';

const MIN_FILL_ROWS = 6;
const DIFF_THRESHOLD = 0.75;
const DIFF_THRESHOLD_NEGATIVE = -0.5;
const DIFF_THRESHOLD_FIXED = 0.5;

const READINESS = {
  READY: 'READY',
  INDIFFERENT: 'INDIFFERENT',
  NOT_READY: 'NOT_READY',
  UNKNOWN: 'UNKNOWN',
};

const calculateDiff = (score, avg, max) => {
  if (getConfig().fixedScaleReadiness) {
    return (score - avg) / max;
  } else {
    if (score > avg) {
      return (score - avg) / (max - avg);
    }
    return (score - avg) / avg;
  }
};

const getReadiness = diff => {
  if (isNum(diff)) {
    if (getConfig().fixedScaleReadiness) {
      if (diff > DIFF_THRESHOLD_FIXED) {
        return READINESS.READY;
      } else if (diff < 0) {
        return READINESS.NOT_READY;
      }
      return READINESS.INDIFFERENT;
    } else {
      if (diff > DIFF_THRESHOLD) {
        return READINESS.READY;
      } else if (diff <= DIFF_THRESHOLD_NEGATIVE) {
        return READINESS.NOT_READY;
      }
    }
    return READINESS.INDIFFERENT;
  }
  return READINESS.UNKNOWN;
};

const getReadinessImage = readiness => {
  let src;
  switch (readiness) {
    case READINESS.READY:
      src = '/images/emotion/03_green.svg';
      break;
    case READINESS.INDIFFERENT:
      src = '/images/emotion/02_yellow.svg';
      break;
    case READINESS.NOT_READY:
      src = '/images/emotion/01_red.svg';
      break;
    default:
      return null;
  }
  return <Image src={src} mx={'auto'} />;
};

const getSuggestion = (topics = []) => {
  if (topics.length > 0) {
    const topicsByWorst = [...topics].sort((a, b) => a.readinessDiff - b.readinessDiff);
    const topWorst = topicsByWorst
      .slice(0, 4)
      .map((row, index) => `${index + 1}. ${row.title}`)
      .join('\n');

    const readinessFreq = _groupBy(topics, topic => topic.readiness);
    const maxFrequentReadiness = Object.keys(readinessFreq).reduce((a, b) =>
      readinessFreq[a].length > readinessFreq[b].length ? a : b,
    );

    return { topics: topWorst, mode: maxFrequentReadiness };
  }
  return { topics: '', mode: READINESS.UNKNOWN };
};

const HeaderCell = props => (
  <TableCell color={COLOR.greyscale.grey1} fontWeight="bold" {...props} />
);

const TableHeadSection = () => (
  <TableHead>
    <TableRow bgColor={COLOR.gridGrey} height="48px">
      <HeaderCell cellBorder>ชื่อบท</HeaderCell>
      <HeaderCell>เปรียบเทียบคะแนนแต่ละบท</HeaderCell>
      <HeaderCell textAlign="center">คะแนนที่ได้ / เต็ม</HeaderCell>
      <HeaderCell textAlign="center" cellBorder>
        ความพร้อม
      </HeaderCell>
      <HeaderCell>ข้อมูลเพิ่มเติม</HeaderCell>
    </TableRow>
  </TableHead>
);

const InstructionImageContainer = styled(Box)`
  padding: 16px;
  position: absolute;
  bottom: 0;
  left: 0;
`;
const InstructionImage = styled(Image)`
  border: 1px solid ${COLOR.greyscale.grey4};
`;

const EmptyRow = () => (
  <TableRow noBorder={true}>
    <TableCell cellBorder={true} />
    <TableCell />
    <TableCell />
    <TableCell cellBorder={true} />
  </TableRow>
);

const DataRow = props => {
  const { id, title, score, fullScore, maxFullScore, averageScore, readiness } = props;
  return (
    <TableRow noBorder key={id} height={'60px'}>
      <TableCell cellBorder>{title}</TableCell>
      <TableCell style={{ overflow: 'visible' }}>
        <ScoreBar
          width={`${(fullScore / maxFullScore) * 100}%`}
          score={score}
          averageScore={averageScore}
          fullScore={fullScore}
          backgroundColor="greyscale.grey4"
        />
      </TableCell>
      <TableCell>
        <SplitScore score={score} fullScore={fullScore} align="center" />
      </TableCell>
      <TableCell cellBorder>{getReadinessImage(readiness)}</TableCell>
    </TableRow>
  );
};

const HalfBox = props => (
  <Box
    width={[1, 1 / 2]}
    px={[SPACE.medium, null, null, SPACE.large]}
    py={SPACE.medium}
    {...props}
  />
);

const EmotionGuideRow = ({ imageSrc, text }) => (
  <Flex mb={20} alignItems="center">
    <Image src={imageSrc} />
    <Text ml={SPACE.medium}>{text}</Text>
  </Flex>
);

const SuggestionTitle = props => <Text.Subtitle mb={SPACE.medium} fontWeight="bold" {...props} />;
const SuggestBodyText = props => <Text is="p" mb={SPACE.medium} {...props} />;

const getSuggestionDisplay = (topics, mode) => {
  let top, bottom;
  switch (mode) {
    case READINESS.READY:
      top = `คะแนนของน้องอยู่ในเกณฑ์ดีมาก พร้อมที่จะสอบแล้วนะ! แต่ถ้าน้องอยากมั่นใจในการสอบมากขึ้น
      พี่อยากแนะนำให้น้องอ่านบท`;
      bottom = `และฝึกทำโจทย์เพิ่มเติม น้องจะได้มีความพร้อมสำหรับการสอบอย่างเต็มที่ครับ สู้ สู้ !
      คณะในฝันอยู่ไม่ไกลเกินเอื้อมแล้วน้า ☺️`;
      break;
    case READINESS.INDIFFERENT:
      top = `คะแนนของน้องอยู่ในเกณฑ์ปานกลาง อีกนิดเดียวก็พร้อมที่จะสอบแล้วนะ! แต่ถ้าอยากอัพคะแนนให้มากกว่านี้ 
      พี่แนะนำให้น้องเตรียมอ่านและฝึกทำโจทย์เพิ่มดังนี้ครับ`;
      bottom = `เป็นกำลังใจให้น้องนะครับ สู้ ๆ เพื่อคณะในฝันของเรา ☺️`;
      break;
    case READINESS.NOT_READY:
      top = `คะแนนของน้องอยู่ในเกณฑ์ที่ต้องเพิ่มความพร้อมอีกมาก!
      แต่ไม่เป็นไร ยังพอมีเวลาที่จะอัพคะแนนเพิ่มได้
      สิ่งที่น้องต้องเตรียมพร้อมให้มากขึ้น คือ อ่านหนังสือและฝึกทำโจทย์ให้มากกว่าเดิม
      โดยบทที่น้องควรอ่านมีดังนี้ครับ`;
      bottom = `คณะในฝันอยู่ไม่ไกลเกินเอื้อม สู้ให้เต็มที่ครับ!`;
      break;

    default:
      top = `จากคะแนนของน้องพี่ขอแนะนำ 4 อันดับแรกที่น้องควรเริ่มอ่านทำความเข้าใจ
      หรือฝึกทำโจทย์เพิ่มจากบทตามลำดับต่อไปนี้`;
      bottom = `น้องจะได้มีความพร้อมสำหรับการสอบอย่างเต็มที่ครับ คณะในฝันอยู่ไม่ไกล`;
      break;
  }
  return (
    <Fragment>
      <Image
        src="https://tcas-assets.skooldio.com/images/score_by_topic_suggestion/score_suggestion.png"
        alt="คำแนะนำจากพี่น็อต TCASter"
        maxWidth={160}
        mx="auto"
        mb={SPACE.medium}
      />
      <SuggestBodyText>{top}</SuggestBodyText>
      <SuggestBodyText style={{ whiteSpace: 'pre-line' }}>{topics}</SuggestBodyText>
      <SuggestBodyText>{bottom}</SuggestBodyText>
    </Fragment>
  );
};

const SuggestionBox = ({ suggestion: { topics, mode }, ...restProps }) => (
  <Flex flexWrap="wrap" bg="greyscale.grey5" {...restProps}>
    <HalfBox>
      <SuggestionTitle>คำแนะนำจากพี่น็อต TCASter</SuggestionTitle>
      {getSuggestionDisplay(topics, mode)}
    </HalfBox>
    <HalfBox>
      <SuggestionTitle>วิเคราะห์ความพร้อมในแต่ละบท</SuggestionTitle>
      <Box mt={SPACE.small}>
        <EmotionGuideRow
          imageSrc="/images/emotion/01_red.svg"
          text="ต้องเพิ่มความพร้อมอีกมาก Fighting!!!"
        />
        <EmotionGuideRow
          imageSrc="/images/emotion/02_yellow.svg"
          text="พร้อมกลางๆ (เตรียมตัวเพิ่มอีกนิด)"
        />
        <EmotionGuideRow
          imageSrc="/images/emotion/03_green.svg"
          text="พร้อมมาก (ทบทวนเพิ่มความมั่นใจได้อีก)"
        />
      </Box>
    </HalfBox>
  </Flex>
);

const TableBodySection = ({ rows, maxScore, minScore, averageScore, sd, scoreVariant }) => {
  // This is a workaround to make sure that last column height will not be too short.
  const fillRows = rows.length <= MIN_FILL_ROWS ? fillArray(rows, MIN_FILL_ROWS) : rows;
  const maxFullScore = rows.map(row => row.fullScore).reduce((max, cur) => Math.max(max, cur), 1);
  const numVariant = 'twoDecimals';

  return (
    <TableBody>
      <TableRow noBorder={true} height={'0px'}>
        <TableCell empty={true} />
        <TableCell empty={true} />
        <TableCell empty={true} />
        <TableCell empty={true} />
        <TableCell
          rowSpan={fillRows.length + 1}
          // width={'240px'}
          minWidth={'180px'}
          verticalAlign={'top'}
        >
          <Number label={'คะแนนสูงสุด'} value={maxScore} variant={numVariant} />
          <Number label={'คะแนนต่ำสุด'} value={minScore} variant={numVariant} />
          <Number label={'คะแนนเฉลี่ย'} value={averageScore} variant={numVariant} />
          <Number label={'SD'} value={sd} variant={numVariant} />
          <InstructionImageContainer>
            <InstructionImage
              src={'/images/score-instructions.png'}
              srcSet={
                '/images/score-instructions.png 189w, /images/score-instructions@2x.png 378w, /images/score-instructions@3x.png 567w'
              }
              alt={'How to read scores'}
            />
          </InstructionImageContainer>
        </TableCell>
      </TableRow>
      {fillRows.map((row, index) => {
        return !row ? (
          <EmptyRow key={index} />
        ) : (
          <DataRow key={index} maxFullScore={maxFullScore} {...row} />
        );
      })}
    </TableBody>
  );
};
const ScoreSummaryTable = ({
  rows,
  minScore,
  maxScore,
  averageScore,
  sd,
  maxFullScore,
  scoreVariant,
}) => {
  const rowsWithDiff = rows.map(row => {
    let diff;
    const { score, averageScore, fullScore } = row;
    if (isNum(score) && isNum(averageScore) && isNum(fullScore)) {
      // diff = (score - averageScore) / fullScore;
      diff = calculateDiff(score, averageScore, fullScore);
    }
    return { ...row, readinessDiff: diff, readiness: getReadiness(diff) };
  });
  return (
    <Box>
      <Box style={{ overflow: 'auto' }}>
        <Table border padding={'checkbox'}>
          <TableHeadSection />
          <TableBodySection
            rows={rowsWithDiff}
            maxScore={maxScore}
            minScore={minScore}
            averageScore={averageScore}
            sd={sd}
            maxFullScore={maxFullScore}
            scoreVariant={scoreVariant}
          />
        </Table>
      </Box>
      <SuggestionBox suggestion={getSuggestion(rowsWithDiff)} />
    </Box>
  );
};

ScoreSummaryTable.propTypes = {
  rows: PropTypes.array,
  minScore: PropTypes.number,
  maxScore: PropTypes.number,
  averageScore: PropTypes.number,
  sd: PropTypes.number,
  maxFullScore: PropTypes.number,
  suggestion: PropTypes.string,
  scoreVariant: PropTypes.string,
};

ScoreSummaryTable.defaultProps = {
  rows: [],
};

export default ScoreSummaryTable;
